import { LegacyGalleryResource } from "./LegacyGalleryResource"
import { GalleryPrivacy } from "./GalleryPrivacy"
import Upload from "../upload/Upload"
import { GalleryResource } from "./GalleryResource"
import ImageAsset from "../common/ImageAsset"
import * as ColorUtil from "../color/ColorUtil"
import { ShareOption } from "../sharing/ShareOption"
import { UploadResource } from "../upload/UploadResource"
import { ErrorCallback, fetchGet } from "../common/Requests"

export class Gallery {
  code: string
  title: string
  createdAt: number
  privacy: GalleryPrivacy
  hashtag: string
  friendlyUrl: string | null
  latestUploadCreatedAt: Date | null
  design: {
    textColor: string
    backgroundColor: string
    topImageAsset: ImageAsset | null
    topUrl: string
    bottomImageAsset: ImageAsset | null
    bottomUrl: string
    showSimpleBoothLogo: boolean
  }
  ads:{
    profileAdDisplayingEnabled: boolean
  }
  sharing: {
    instagramEnabled?: boolean
    facebookEnabled?: boolean
    twitterEnabled?: boolean
    downloadEnabled?: boolean
    copyLinkEnabled?: boolean
    linkedInEnabled?: boolean
  }
  dates: string[] | null
  moderated: boolean
  finePrint?: string | null
  totalUploads: number
  userId: null | number
  
  currentUser: {
    canEdit: boolean
    isAdmin: boolean
  }

  uploads: Upload[]
  coverMedia: ImageAsset | null

  links: {
    webSelf?: string | null // null when private
    liveFeed?: string
    nextUploads?: string
    latestUploads?: string
  }

  user?: {
    username: string
    profilePublic: boolean
  }

  constructor() {
    this.hashtag = "#simplebooth"
    this.design = {
      textColor: "#000000",
      backgroundColor: "#FFFFFF",
      topImageAsset: null,
      topUrl: null,
      bottomImageAsset: null,
      bottomUrl: null,
      showSimpleBoothLogo: true
    }
    this.ads = {
      profileAdDisplayingEnabled: false
    }
    this.sharing = {
      instagramEnabled: true,
      facebookEnabled: true,
      twitterEnabled: true,
      copyLinkEnabled: true,
      downloadEnabled: true,
      linkedInEnabled: true,
    }
    this.links = {}
    this.uploads = []
    this.currentUser = {
      canEdit: false,
      isAdmin: false
    }
  }

  getShareOptions(): ShareOption[] {
    let shareOptions = []
    if (this.sharing.facebookEnabled) {
      shareOptions.push(ShareOption.FACEBOOK)
    }
    if (this.sharing.instagramEnabled) {
      shareOptions.push(ShareOption.INSTAGRAM)
    }
    if (this.sharing.linkedInEnabled) {
      shareOptions.push(ShareOption.LINKEDIN)
    }
    if (this.sharing.twitterEnabled) {
      shareOptions.push(ShareOption.TWITTER)
    }
    if (this.sharing.downloadEnabled) {
      shareOptions.push(ShareOption.DOWNLOAD)
    }
    if (this.sharing.copyLinkEnabled) {
      shareOptions.push(ShareOption.COPY_LINK)
    }

    return shareOptions
  }

  static fromLegacyResource(resource: LegacyGalleryResource): Gallery {
    let gallery = new Gallery()
    gallery.code = resource.code
    gallery.privacy = resource.privacy_level
    gallery.dates = resource.dates

    gallery.createdAt = resource.created
    gallery.title = resource.title
    gallery.hashtag = resource.hashtag
    gallery.friendlyUrl = resource.friendly_url

    gallery.design = {
      textColor: resource.text_color,
      backgroundColor: resource.background_color,
      topImageAsset: null,
      topUrl: resource.top_image_url,
      bottomImageAsset: null,
      bottomUrl: resource.bottom_image_url,
      showSimpleBoothLogo: resource.show_sb_logo
    }
    gallery.design.topImageAsset = resource.top_image ? { url: resource.top_image } : null
    gallery.design.bottomImageAsset = resource.bottom_image ? { url: resource.bottom_image } : null

    gallery.ads.profileAdDisplayingEnabled = resource.ads.profile_ad_displaying_enabled
    gallery.sharing = {
      instagramEnabled: resource.sharing.instagram_on,
      facebookEnabled: resource.sharing.facebook_on,
      twitterEnabled: resource.sharing.twitter_on,
      copyLinkEnabled: resource.sharing.copy_link_on,
      downloadEnabled: resource.sharing.download_on,
      linkedInEnabled: resource.sharing.linked_in_on,
    }
    gallery.finePrint = resource.fine_print
    gallery.totalUploads = resource.total_uploads
    gallery.currentUser = {
      canEdit: resource.user.write_access,
      isAdmin: resource.user.is_admin
    }
    gallery.uploads = resource.uploads.map((embeddedUpload) => Upload.fromLegacyResource(embeddedUpload))

    gallery.links = {
      nextUploads: resource._links.next?.href,
      liveFeed: resource._links["live-feed:self"]?.href,
      webSelf: resource._links["web:self"]?.href,
    }

    gallery.user = {
      username: resource._embedded.user.username,
      profilePublic: resource._embedded.user.profile_public
    }

    return gallery
  }

  static fromResource(resource: GalleryResource): Gallery {
    let gallery = new Gallery()

    gallery.code = resource.code
    gallery.createdAt = Date.parse(resource.createdAt)
    gallery.title = resource.title
    gallery.privacy = resource.privacyLevel
    gallery.hashtag = resource.hashtag
    gallery.latestUploadCreatedAt = new Date(resource.latestUploadCreatedAt)

    gallery.userId = resource.userId
    gallery.design = resource.design
    gallery.ads.profileAdDisplayingEnabled = resource.ads.profileAdDisplayingEnabled
    gallery.sharing = {
      instagramEnabled: resource.socialSharing.instagramEnabled,
      facebookEnabled: resource.socialSharing.facebookEnabled,
      twitterEnabled: resource.socialSharing.twitterEnabled,
      copyLinkEnabled: resource.socialSharing.copyLinkEnabled,
      downloadEnabled: resource.socialSharing.downloadEnabled,
      linkedInEnabled: resource.socialSharing.linkedInEnabled,
    }
    gallery.moderated = resource.moderated
    gallery.finePrint = resource.finePrint
    gallery.totalUploads = resource.totalUploads

    gallery.coverMedia = resource._embedded.coverMedia ?? null

    gallery.links = {
      webSelf: resource._links["web:self"]?.href,
      latestUploads: resource._links.latestUploads.href
    }

    return gallery
  }

  fetchLatestUploads(handleSuccess: (uploads: Upload[]) => void, handleError: ErrorCallback) {
    if (!this.links.latestUploads) {
      throw Error("No link available to fetch latest uploads")
    }

    fetchGet(
      this.links.latestUploads,
      (json) => {
        let resources = json._embedded.uploads as UploadResource[]
        let uploads = resources.map(resource => Upload.fromResource(resource))
        handleSuccess(uploads)
      },
      handleError
    )
  }

  hasDefaultBackgroundColor(): boolean {
    return this.design.backgroundColor.toLowerCase() === "#ffffff"
  }

  hasDefaultColors(): boolean {
    return this.design.textColor.toLowerCase() === "#000000"
      && this.hasDefaultBackgroundColor()
  }

  hasDarkColorScheme(): boolean {
    return ColorUtil.isBrighter(this.design.textColor, this.design.backgroundColor)
  }

  hasDesignCustomizations(): boolean {
    return (
      !this.hasDefaultColors()
      || this.design.topImageAsset != null
      || this.design.bottomImageAsset != null
      || !this.design.showSimpleBoothLogo
    )
  }

  getBackgroundContrastColor(): string {
    if (this.hasDefaultBackgroundColor())
      return "#30aade"
      
    let darkerBg = ColorUtil.brightenHexColor(this.design.backgroundColor, -70)
    let lighterBg = ColorUtil.brightenHexColor(this.design.backgroundColor, 70)
    return ColorUtil.pickHigherContrastOption(darkerBg, lighterBg, this.design.backgroundColor)
  }
}
